import React, { forwardRef } from 'react'
import type { TitleToken } from '@/src/renderer/lib/titleParser'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/src/renderer/components/ui/tooltip'
import './TitleMarks.scss'

export interface TitleMarksProps {
  /** Derselbe Text wie im Titelfeld darüber. */
  text: string
  /** Die erkannten Teile — sie liegen hinterlegt. */
  tokens: TitleToken[]
  className?: string
  /**
   * Über diesem Teil steht der Zeiger (Anfang im Text) — er bekommt den Tooltip `hint`. Die Kopie
   * nimmt selbst keine Zeiger; wann er erscheint, entscheidet die Zeile.
   */
  hintAt?: number | null
  hint?: string
  /**
   * Der Farbton eines Teils — derselbe wie der seines Chips (`urgent`, `overdue`, `recurrence-fixed`,
   * `attention-high` …). Ohne Ton: die Farbe des Bereichs.
   */
  toneOf?: (token: TitleToken) => string | undefined
  /**
   * Eine leere Stelle im Text (`anchorRef`), an der die Vorschläge hängen — das `#`, `/` oder `@`, hinter
   * dem gerade gesucht wird (Schritt C). Liegt sie in einem erkannten Teil, steht sie vor ihm.
   */
  anchorAt?: number | null
  anchorRef?: React.Ref<HTMLSpanElement>
}

/**
 * **Die Markierung im Titel** (#234): eine Kopie des Textes HINTER dem durchsichtigen Titelfeld, in
 * derselben Schrift und Breite. Unsichtbare Buchstaben, nur die erkannten Teile tragen einen Grund —
 * im Ton ihres Chips, denn die Farbe sagt etwas (Deadline heute, überfällig, Art der Wiederholung). So sieht man die Markierung im echten Feld, ohne dass das Feld selbst
 * etwas anderes als Text enthält (Caret, Auswahl, Rückgängig bleiben die des Browsers).
 *
 * Die Typografie setzt die Zeile über `className`; die Kopie muss genau so umbrechen wie das Feld.
 *
 * @example
 * <div className="task-entry__title-field">
 *   <TitleMarks className="task-entry__title-marks" text={title} tokens={parsed.tokens} />
 *   <textarea className="task-entry__title-input" value={title} … />
 * </div>
 */
export const TitleMarks = forwardRef<HTMLDivElement, TitleMarksProps>(({ text, tokens, className, hintAt, hint, toneOf, anchorAt, anchorRef }, ref) => {
  const parts: React.ReactNode[] = []
  let cursor = 0
  let anchored = anchorAt == null
  const anchor = <span key="anchor" ref={anchorRef} className="title-marks__anchor" />
  const plain = (from: number, to: number) => {
    if (!anchored && anchorAt! >= from && anchorAt! < to) {
      anchored = true
      parts.push(text.slice(from, anchorAt!), anchor, text.slice(anchorAt!, to))
    } else if (to > from) {
      parts.push(text.slice(from, to))
    }
  }
  for (const token of tokens) {
    plain(cursor, token.start)
    if (!anchored && anchorAt! < token.end) { anchored = true; parts.push(anchor) }
    const tone = toneOf?.(token)
    const mark = (
      <mark className={`title-marks__mark${tone ? ` title-marks__mark--${tone}` : ''}`} data-start={token.start}>
        {text.slice(token.start, token.end)}
      </mark>
    )
    parts.push(hint && hintAt === token.start ? (
      <Tooltip key={token.start} open>
        <TooltipTrigger asChild>{mark}</TooltipTrigger>
        <TooltipContent>{hint}</TooltipContent>
      </Tooltip>
    ) : <React.Fragment key={token.start}>{mark}</React.Fragment>)
    cursor = token.end
  }
  plain(cursor, text.length)
  if (!anchored) parts.push(anchor)

  return (
    <div ref={ref} className={`title-marks${className ? ` ${className}` : ''}`} aria-hidden="true">
      {parts}
      {/* Hält einen Zeilenumbruch am Ende offen, wie beim Notizfeld. */}
      {'​'}
    </div>
  )
})
TitleMarks.displayName = 'TitleMarks'
